import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchTruckList, fetchTruckListConfig, fetchTruckListDispatchers, updateTruckListConfig } from "@/services/trucklistApi";

const TRUCK_LIST_CONFIG_KEY = ["trucklist-config"];

// Server-driven public truck list for a carrier. All filter / sort / pagination
// state is passed through to the backend; the cache key includes every param so
// switching filters fetches a fresh page rather than hitting a stale cache.
// `placeholderData: keepPreviousData` makes pagination feel snappier — the old
// page stays visible while the next one loads.
export const useTruckList = (slug, params) =>
  useQuery({
    queryKey: ["trucklist", slug, params],
    queryFn: () => fetchTruckList(slug, params),
    enabled: !!slug,
    retry: false,
    placeholderData: keepPreviousData,
  });

// Dispatchers available for the dispatcher filter dropdown. Scoped to the whole
// truck set for the company, independent of the current view.
export const useTruckListDispatchers = (slug) =>
  useQuery({
    queryKey: ["trucklist", slug, "dispatchers"],
    queryFn: () => fetchTruckListDispatchers(slug),
    enabled: !!slug,
    retry: false,
  });

// Caller's own company truck-list config. `data` is null when the backend returns
// 404 (no row yet OR user has no company) — the UI treats that as "feature off".
export const useTruckListConfig = () =>
  useQuery({
    queryKey: TRUCK_LIST_CONFIG_KEY,
    queryFn: fetchTruckListConfig,
    retry: false,
  });

// Toggles the caller's truck-list opt-in. On success the cache is primed with the
// returned row, so the toggle reflects the new state (and the slug) immediately
// without a follow-up GET.
export const useUpdateTruckListConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTruckListConfig,
    onSuccess: (data) => queryClient.setQueryData(TRUCK_LIST_CONFIG_KEY, data),
  });
};
